<h2 mat-dialog-title>
  <mat-icon>business</mat-icon>
  Add New Party
</h2>

<mat-dialog-content class="dialog-content">
  <!-- Loading Spinner -->
  @if (loading || saving) {
    <div class="loading-overlay">
      <mat-spinner diameter="40"></mat-spinner>
      <p>{{ saving ? 'Saving party...' : 'Loading...' }}</p>
    </div>
  }

  <!-- Top Action Buttons -->
  <div class="top-actions">
    <button mat-stroked-button color="accent" (click)="cancel()" type="button">
      <mat-icon>close</mat-icon>
      Cancel
    </button>
    <button mat-raised-button color="primary"
            [disabled]="saving"
            (click)="onSubmit()" type="button">
      <mat-icon>save</mat-icon>
      Save Party
    </button>
  </div>

  <form [formGroup]="form">

    <!-- Basic Information Section -->
    <mat-card class="form-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>info</mat-icon>
          Basic Information
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="form-grid">
          <!-- Vendor Number (readonly) -->
          @if (form.get('vendorNum')?.value) {
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Vendor Number</mat-label>
              <input matInput formControlName="vendorNum" readonly>
              <mat-icon matSuffix color="warn">lock</mat-icon>
            </mat-form-field>
          }

          <!-- Legal Name (stored as `name`) -->
          <mat-form-field appearance="outline">
            <mat-label>Legal Name <span class="required-indicator">*</span></mat-label>
            <input matInput formControlName="name" placeholder="Enter legal name" required>
            <mat-icon matSuffix>business</mat-icon>
            @if (form.get('name')?.hasError('required')) {
              <mat-error>Legal name is required</mat-error>
            }
          </mat-form-field>

          <!-- Accounting Company Name -->
          <mat-form-field appearance="outline">
            <mat-label>Accounting Company Name</mat-label>
            <input matInput formControlName="accountingCompanyName" placeholder="Name used in accounting / AP">
            <mat-icon matSuffix>receipt_long</mat-icon>
          </mat-form-field>

          <!-- DBA -->
          <mat-form-field appearance="outline">
            <mat-label>DBA</mat-label>
            <input matInput formControlName="dba" placeholder="Doing Business As">
            <mat-icon matSuffix>store</mat-icon>
          </mat-form-field>

          <!-- Formerly Known As -->
          <mat-form-field appearance="outline">
            <mat-label>Formerly Known As (FKA)</mat-label>
            <input matInput formControlName="formallyKnownAs" placeholder="Previous name">
          </mat-form-field>

          <!-- Standing -->
          <mat-form-field appearance="outline">
            <mat-label>Standing</mat-label>
            <mat-select formControlName="standing">
              @for (standing of partyStanding; track standing) {
                <mat-option [value]="standing">{{ standing }}</mat-option>
              }
            </mat-select>
            <mat-icon matSuffix>verified</mat-icon>
          </mat-form-field>

          <!-- Party Type -->
          <mat-form-field appearance="outline">
            <mat-label>Party Type</mat-label>
            <mat-select formControlName="partyType">
              @for (type of partyTypes; track type) {
                <mat-option [value]="type">{{ type }}</mat-option>
              }
            </mat-select>
            <mat-icon matSuffix>category</mat-icon>
          </mat-form-field>

          <!-- Website -->
          <mat-form-field appearance="outline" class="span-2">
            <mat-label>Website</mat-label>
            <input matInput formControlName="website" placeholder="https://example.com">
            <mat-icon matSuffix>language</mat-icon>
          </mat-form-field>
        </div>
      </mat-card-content>
    </mat-card>

    <!-- Identification Numbers Section -->
    <mat-card class="form-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>badge</mat-icon>
          Identification Numbers
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="form-grid">
          <mat-form-field appearance="outline">
            <mat-label>DUNS Number</mat-label>
            <input matInput formControlName="dunsNum" placeholder="9-digit DUNS">
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>NPI Number</mat-label>
            <input matInput formControlName="npiNum" placeholder="10-digit NPI">
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>CCN Number</mat-label>
            <input matInput formControlName="ccnNum" placeholder="CCN">
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>TIN</mat-label>
            <input matInput formControlName="tin" placeholder="Tax ID Number">
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Tax Exempt Certificate #</mat-label>
            <input matInput formControlName="taxExemptNum">
          </mat-form-field>
        </div>
      </mat-card-content>
    </mat-card>

    <!-- Relationship Section -->
    <mat-card class="form-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>account_tree</mat-icon>
          Relationships
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="toggle-section">
          <mat-slide-toggle formControlName="isSubContractor" color="primary">
            <span class="toggle-label">Sub-Contractor</span>
          </mat-slide-toggle>

          <mat-slide-toggle formControlName="isChildOfParentParty" color="primary">
            <span class="toggle-label">Child of Parent Party</span>
          </mat-slide-toggle>
        </div>

        @if (form.get('isChildOfParentParty')?.value) {
          <div class="parent-party-section">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Parent Party</mat-label>
              <mat-select formControlName="parentVendor"
                         (selectionChange)="parentVendorChanged($event)">
                @for (vendor of allParties$ | async; track vendor.vendorNum) {
                  <mat-option [value]="vendor.vendorNum">
                    {{ vendor | partyLabel }}
                  </mat-option>
                }
              </mat-select>
              <mat-icon matSuffix>business_center</mat-icon>
            </mat-form-field>
          </div>
        }
      </mat-card-content>
    </mat-card>

    <!-- Primary Address Section -->
    <mat-card class="form-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>location_on</mat-icon>
          Primary Address
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="form-grid">
          <mat-form-field appearance="outline" class="span-2">
            <mat-label>Street Address</mat-label>
            <input matInput formControlName="address" placeholder="123 Main Street">
            <mat-icon matSuffix>home</mat-icon>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>City</mat-label>
            <input matInput formControlName="city" placeholder="City">
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>State</mat-label>
            <mat-select formControlName="state">
              @for (state of geographicStates; track state) {
                <mat-option [value]="state">{{ state }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Zip Code</mat-label>
            <input matInput formControlName="zip" placeholder="12345">
          </mat-form-field>
        </div>
      </mat-card-content>
    </mat-card>

    <!-- Secondary Address Section -->
    <mat-card class="form-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>add_location</mat-icon>
          Secondary Address
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="form-grid">
          <mat-form-field appearance="outline" class="span-2">
            <mat-label>Street Address</mat-label>
            <input matInput formControlName="secondaryAddress" placeholder="123 Second Street">
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>City</mat-label>
            <input matInput formControlName="secondaryCity" placeholder="City">
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>State</mat-label>
            <mat-select formControlName="secondaryState">
              @for (state of geographicStates; track state) {
                <mat-option [value]="state">{{ state }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Zip Code</mat-label>
            <input matInput formControlName="secondaryZip" placeholder="12345">
          </mat-form-field>
        </div>
      </mat-card-content>
    </mat-card>

    <!-- Contact Information Section -->
    <mat-card class="form-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>phone</mat-icon>
          Contact Information
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="form-grid">
          <mat-form-field appearance="outline">
            <mat-label>Primary Phone</mat-label>
            <input matInput formControlName="phonePrimary" placeholder="(555) 123-4567">
            <mat-icon matSuffix>call</mat-icon>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Secondary Phone</mat-label>
            <input matInput formControlName="phoneSecondary" placeholder="(555) 987-6543">
            <mat-icon matSuffix>phone</mat-icon>
          </mat-form-field>
        </div>
      </mat-card-content>
    </mat-card>

    <!-- Team Assignment Section -->
    <mat-card class="form-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>people</mat-icon>
          Team Assignment
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="form-grid">
          <mat-form-field appearance="outline">
            <mat-label>Owner</mat-label>
            <input type="text" matInput
              formControlName="owner"
              [matAutocomplete]="ownerAuto"
              (input)="filterUsers($event)"
              placeholder="Select owner">
            <mat-icon matSuffix>person</mat-icon>
            <mat-autocomplete #ownerAuto="matAutocomplete" [displayWith]="displayUserFn">
              @for (user of filteredUsers; track user.email) {
                <mat-option [value]="user">
                  <span class="user-option">{{ user.shortFriendlyName }}</span>
                </mat-option>
              } @empty {
                <mat-option disabled>No users found</mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Assessor</mat-label>
            <input type="text" matInput
              formControlName="assessor"
              [matAutocomplete]="assessorAuto"
              (input)="filterUsers($event)"
              placeholder="Select assessor">
            <mat-icon matSuffix>assignment</mat-icon>
            <mat-autocomplete #assessorAuto="matAutocomplete" [displayWith]="displayUserFn">
              @for (user of filteredUsers; track user.email) {
                <mat-option [value]="user">
                  <span class="user-option">{{ user.friendlyName }}</span>
                </mat-option>
              } @empty {
                <mat-option disabled>No users found</mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Final Reviewer</mat-label>
            <input type="text" matInput
              formControlName="finalReviewer"
              [matAutocomplete]="finalReviewerAuto"
              (input)="filterUsers($event)"
              placeholder="Select reviewer">
            <mat-icon matSuffix>check_circle</mat-icon>
            <mat-autocomplete #finalReviewerAuto="matAutocomplete" [displayWith]="displayUserFn">
              @for (user of filteredUsers; track user.email) {
                <mat-option [value]="user">
                  <span class="user-option">{{ user.friendlyName }}</span>
                </mat-option>
              } @empty {
                <mat-option disabled>No users found</mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>
        </div>
      </mat-card-content>
    </mat-card>

    <!-- Organization Details Section -->
    <mat-card class="form-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>corporate_fare</mat-icon>
          Organization Details
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="form-grid">
          <mat-form-field appearance="outline">
            <mat-label>Annual Revenues</mat-label>
            <input matInput formControlName="annualRevenues" placeholder="$1,000,000">
            <mat-icon matSuffix>attach_money</mat-icon>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Number of Employees</mat-label>
            <input matInput formControlName="numEmployees" type="number" placeholder="100">
            <mat-icon matSuffix>groups</mat-icon>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>State of Incorporation</mat-label>
            <mat-select formControlName="stateOfIncorporation">
              @for (state of geographicStates; track state) {
                <mat-option [value]="state">{{ state }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Public or Private</mat-label>
            <input matInput formControlName="publicOrPrivate" placeholder="Public/Private">
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>County</mat-label>
            <input matInput formControlName="county" placeholder="County name">
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Country</mat-label>
            <input matInput formControlName="country" placeholder="USA">
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Non-Profit or For Profit</mat-label>
            <input matInput formControlName="nonOrForProfit" placeholder="For Profit/Non-Profit">
          </mat-form-field>

          <mat-form-field appearance="outline" class="span-2">
            <mat-label>Specialty</mat-label>
            <mat-select formControlName="specialty" multiple>
              @for (element of specialties; track element) {
                <mat-option [value]="element">{{ element }}</mat-option>
              }
            </mat-select>
            <mat-icon matSuffix>local_hospital</mat-icon>
          </mat-form-field>
        </div>
      </mat-card-content>
    </mat-card>

    <!-- Additional Details Section -->
    <mat-card class="form-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>description</mat-icon>
          Additional Details
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Description</mat-label>
          <textarea matInput
                    formControlName="description"
                    rows="4"
                    placeholder="Enter party description"></textarea>
        </mat-form-field>

        <div class="checkbox-group">
          <mat-checkbox formControlName="baaRequired" color="primary">
            <span class="checkbox-label">Missing BAA</span>
          </mat-checkbox>

          <mat-checkbox formControlName="coiRequired" color="primary">
            <span class="checkbox-label">Missing COI</span>
          </mat-checkbox>
        </div>
      </mat-card-content>
    </mat-card>

  </form>
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-stroked-button color="accent" (click)="cancel()">
    <mat-icon>close</mat-icon>
    Cancel
  </button>
  <button mat-raised-button color="primary"
          [disabled]="saving"
          (click)="onSubmit()">
    <mat-icon>save</mat-icon>
    Save Party
  </button>
</mat-dialog-actions>
